import { ArrowRight, Check, CircleDot, GitPullRequest, SearchCheck } from "lucide-react"
import type { Metadata } from "next"
import Link from "next/link"

export const metadata: Metadata = {
  title: "How it works",
  description: "Follow Resolto's evidence-first path from issue intake to governed resolution.",
}

const stages = [
  ["Intake", "Identify the issue type, affected record, urgency, and what is still missing."],
  [
    "Clarification",
    "Ask the smallest issue-specific question set and reuse context already present.",
  ],
  ["Playbook", "Rank published diagnostic paths and explain the selected match."],
  [
    "Safe read plan",
    "Validate every planned check against tenant policy and connector capability.",
  ],
  ["Evidence", "Record facts, inferences, contradictions, missing data, trust, and source links."],
  [
    "Diagnosis",
    "Present the likely cause, calibrated confidence, evidence IDs, impact, and next step.",
  ],
  [
    "Outcome",
    "Explain, request approval, escalate, or flag a recurring defect for developer review.",
  ],
  [
    "Learning",
    "Let a developer confirm or correct the cause before any playbook update is published.",
  ],
]

export default function HowItWorksPage() {
  return (
    <main id="main-content">
      <section className="marketing-page-hero">
        <div className="marketing-container">
          <p className="section-kicker">How it works</p>
          <h1>A visible chain of decisions from first report to final outcome.</h1>
          <p>
            The model can plan and explain. Approved typed tools perform every read or action, and
            every material claim points back to evidence.
          </p>
        </div>
      </section>
      <section className="marketing-section">
        <div className="marketing-container investigation-sequence">
          <ol>
            {stages.map(([title, body], index) => (
              <li key={title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h2>{title}</h2>
                  <p>{body}</p>
                </div>
                {index < stages.length - 1 && <ArrowRight aria-hidden="true" />}
              </li>
            ))}
          </ol>
        </div>
      </section>
      <section className="marketing-section evidence-section">
        <div className="marketing-container decision-table">
          <div>
            <SearchCheck aria-hidden="true" />
            <h2>Explanation</h2>
            <p>Enough evidence to explain the cause without changing a record.</p>
            <span>
              <Check aria-hidden="true" />
              No approval needed
            </span>
          </div>
          <div>
            <CircleDot aria-hidden="true" />
            <h2>Controlled action</h2>
            <p>An allowlisted, bounded action with exact preview and configured authority.</p>
            <span>
              <Check aria-hidden="true" />
              Approval and verification required
            </span>
          </div>
          <div>
            <GitPullRequest aria-hidden="true" />
            <h2>Developer escalation</h2>
            <p>Evidence, likely cause, open questions, reproduction, and bounded next steps.</p>
            <span>
              <Check aria-hidden="true" />
              Normal engineering controls remain authoritative
            </span>
          </div>
        </div>
      </section>
      <section className="marketing-section compact-cta-section">
        <div className="marketing-container compact-cta">
          <div>
            <h2>Bring one recurring interruption.</h2>
            <p>We will scope the exact records, playbook, evidence, and safe outcome.</p>
          </div>
          <Link className="button button-primary" href="/contact">
            Discuss the workflow
            <ArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>
    </main>
  )
}
